<template>
  <div class="st-file-picker-selected">
    <component
      ref="fileTitle"
      :class="fileClassList"
      :aria-label="`Du har laddat upp filen: ${file.name}. ${
        fileUrl ? 'Klicka för att förhandsgranska i ny tabb.' : ''
      }`"
      :is="fileUrl ? 'a' : 'span'"
      :href="fileUrl"
      :tabindex="fileUrl ? '0' : '-1'"
      target="_blank"
    >
      <StIcon :name="fileIcon" />
      <span aria-hidden="true">{{ file.name }}</span>
    </component>
    <button
      class="st-file-picker-selected__remove-btn"
      @click="emit('file-removed')"
      type="button"
    >
      {{ removeBtnText }}
    </button>
  </div>
</template>
<script setup>
import { ref, onMounted, computed } from "vue";
import { StIcon } from "@/index";

const emit = defineEmits(["file-removed"]);

const fileTitle = ref(null);

const props = defineProps({
  file: [File, Object],
  fileUrl: String,
  focusOnMount: {
    type: Boolean,
    default: true,
  },
  removeBtnText: {
    type: String,
    default: "Ersätt fil",
  },
});

onMounted(() => {
  if (props.focusOnMount) {
    fileTitle.value.focus();
  }
});

const fileIcon = computed(() => {
  const icons = {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      "excel",
    "application/pdf": "pdf",
  };
  return icons[props.file.type] || "download";
});

const fileClassList = computed(() => {
  const file = "st-file-picker-selected__file";
  return [file, props.fileUrl && `${file}--link`];
});
</script>
<style lang="scss" scoped>
@import "../../scss/main.scss";
.st-file-picker-selected {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: var(--container-color-secondary);
  padding: rem-calc(8);
  border-radius: 0;

  &:first-of-type {
    border-top-left-radius: rem-calc(8);
    border-top-right-radius: rem-calc(8);
  }

  &:last-of-type {
    border-bottom-left-radius: rem-calc(8);
    border-bottom-right-radius: rem-calc(8);
  }

  span span {
    @include truncate(2);
  }

  & > * {
    border: var(--border-width) solid transparent;
    border-radius: rem-calc(8);
  }
  &__file {
    display: flex;
    align-items: center;
    line-height: rem-calc(24);
    font-weight: var(--font-weight-semi-bold);
    text-align: left;
    color: var(--text-color-quinary);
    max-width: calc(100% - rem-calc(50));
    flex: 1;
    padding: rem-calc(8);
    outline: none;
    word-break: break-word;
    margin-right: rem-calc(16);

    & > :first-child {
      margin-right: rem-calc(8);
    }

    &--link {
      &:focus {
        border: var(--border-width) solid var(--border-color-quaternary);
      }
      &:hover {
        font-weight: var(--font-weight-bold);
      }
    }
  }

  &__remove-btn {
    font-family: var(--font-primary);
    font-weight: var(--font-weight-semi-bold);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    line-height: rem-calc(24);
    font-size: rem-calc(16);
    color: var(--text-color-quinary);
    white-space: nowrap;
    max-height: rem-calc(34);
    cursor: pointer;
    flex-shrink: 0;
    margin-left: rem-calc(16);

    & > :first-child {
      margin-right: rem-calc(4);
    }

    &:focus {
      outline: none;
      border: var(--border-width) solid var(--border-color-quaternary);
    }
  }

  @media (max-width: rem-calc(400)) {
    flex-direction: row;
    align-items: center;

    &__file {
      margin-right: rem-calc(8);
      max-width: calc(100% - rem-calc(50));
    }

    &__remove-btn {
      margin-left: auto;
      align-self: center;
    }
  }
}
</style>
